import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { resolveAlias, selectBlock, selectLesson } from '../../editorSlice'

/** Последний выбранный экран каждого урока — на время сессии (до перезагрузки). */
const lastBlockByLesson = new Map<number, number>()

// Урок, в который входят сразу в настройки: экран в нём выделять не нужно.
let settingsLessonId: number | null = null
export const enterLessonSettings = (lessonId: number) => {
  settingsLessonId = lessonId
}

/**
 * «Перейти» к уроку (не к блоку): открывает настройки урока. Другой урок — флаг
 * `enterLessonSettings` + `selectLesson`. Уже выбранный — флаг НЕ ставим (смены урока не
 * будет, и он остался бы висеть до следующего захода), экран просто снимаем.
 */
export const useGoToLessonSettings = () => {
  const dispatch = useAppDispatch()
  const store = useStore<{ editor: { selectedLessonId: number | null } }>()
  return (lessonId: number) => {
    if (store.getState().editor.selectedLessonId === lessonId) {
      dispatch(selectBlock(null))
      return
    }
    enterLessonSettings(lessonId)
    dispatch(selectLesson(lessonId))
  }
}

/** Экран, который выделить при входе в урок: последний выбранный, иначе первый. */
export const pickLessonBlock = (
  lessonId: number,
  blocks: ReadonlyArray<{ id: number }>,
): number | null => {
  const remembered = lastBlockByLesson.get(lessonId)
  return (blocks.find((b) => b.id === remembered) ?? blocks[0])?.id ?? null
}

/**
 * При переключении урока сразу выделен экран — последний, что был выбран в этом уроке
 * за сессию, или первый. Без этого урок открывался маскотом «выберите блок», и до экрана
 * нужен был лишний клик по миниатюре. Срабатывает только на СМЕНЕ урока: клик по пустому
 * холсту и настройки урока (шестерёнка на плашке, на ПК повторный клик по плашке) выбор
 * экрана снимают намеренно, их не перебиваем.
 */
export const useLessonLastBlock = () => {
  const dispatch = useAppDispatch()
  const lessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const blockId = useAppSelector((s) => s.editor.selectedBlockId)
  const idAliases = useAppSelector((s) => s.editor.idAliases)
  const { data: blocks } = useGetLessonBlocksQuery(lessonId ?? 0, { skip: lessonId === null })
  // Урок, для которого выбор экрана ещё предстоит (сменился, а список экранов мог не приехать).
  const pendingFor = useRef<number | null>(null)
  const prevLesson = useRef(lessonId)

  useEffect(() => {
    if (lessonId !== null && blockId !== null) lastBlockByLesson.set(lessonId, blockId)
  }, [lessonId, blockId])

  useEffect(() => {
    if (prevLesson.current !== lessonId) {
      // Переезд того же урока live ⇄ черновик (`remapSelection`) — не смена урока:
      // выбор экрана не взводим, иначе настройки урока подменил бы экран.
      const moved =
        prevLesson.current !== null &&
        lessonId !== null &&
        resolveAlias(idAliases, 'lesson', prevLesson.current) === lessonId
      pendingFor.current = settingsLessonId === lessonId || moved ? null : lessonId
      settingsLessonId = null
    }
    prevLesson.current = lessonId
    if (lessonId === null || pendingFor.current !== lessonId) return
    if (blockId !== null) {
      pendingFor.current = null
      return
    }
    if (!blocks) return
    pendingFor.current = null
    const target = pickLessonBlock(lessonId, blocks)
    if (target !== null) dispatch(selectBlock(target))
  }, [lessonId, blockId, blocks, idAliases, dispatch])
}
